import { useState, type JSX } from 'react';
import { Link, NavLink, Outlet } from 'react-router';
import { authApi } from '../api/client.js';
import { FormMessage } from '../components/FormMessage.js';
import { useAuth } from '../auth/context.js';
import { useI18n } from '../i18n/context.js';
import { AccountMenu } from '../components/AccountMenu.js';
import { LegalLinks } from '../components/LegalLinks.js';
import { BrandLogo } from '../components/BrandLogo.js';

export function AppLayout(): JSX.Element {
  const { user } = useAuth();
  const { t } = useI18n();
  const [resent, setResent] = useState(false);

  return (
    <div className="min-h-screen">
      {/* The header's content sits in the page column, so the logo lines up with what is below it. */}
      <header className="border-b border-[var(--kitup-color-muted)]/20 px-4 py-3">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
          <Link to="/app" aria-label={t.app.name} className="inline-flex no-underline">
            <BrandLogo />
          </Link>

          {/*
            The library sections are the product's places, not account settings: on a wide
            screen they sit in the header. On a phone they stay in the account menu.
          */}
          {user ? (
            <nav aria-label={t.app.sections} className="mr-auto hidden gap-1 text-sm md:flex">
              {(
                [
                  ['/app', t.books.title],
                  ['/app/characters', t.characters.title],
                  ['/app/things', t.things.title],
                  ['/app/locations', t.locations.title],
                ] as const
              ).map(([to, label]) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === '/app'}
                  className={({ isActive }) =>
                    `rounded-[var(--kitup-radius-sm)] px-3 py-1.5 font-medium no-underline ${
                      isActive
                        ? 'bg-[var(--kitup-color-accent)]/10 text-[var(--kitup-color-ink)]'
                        : 'text-[var(--kitup-color-muted)] hover:text-[var(--kitup-color-ink)]'
                    }`
                  }
                >
                  {label}
                </NavLink>
              ))}
            </nav>
          ) : null}

          {user ? <AccountMenu name={user.name} isAdmin={user.role === 'admin'} /> : null}
        </div>
      </header>

      {/*
        Вход до подтверждения почты разрешён, генерация — нет (01-SPEC.md §5.2, §7).
        Баннер объясняет, почему часть возможностей закрыта, вместо молчаливой ошибки.
      */}
      {user && !user.emailVerified ? (
        <div className="px-4 pt-4">
          <FormMessage kind="error">{t.auth.verify.banner}</FormMessage>
          {resent ? (
            <p role="status" className="mt-2 text-sm text-[var(--kitup-color-muted)]">
              {t.auth.verify.resent}
            </p>
          ) : (
            <button
              type="button"
              onClick={() => {
                void authApi.resendVerification().finally(() => setResent(true));
              }}
              className="mt-2 text-sm underline"
            >
              {t.auth.verify.resend}
            </button>
          )}
        </div>
      ) : null}

      <main className="px-4 py-6">
        <Outlet />
      </main>
      <footer className="border-t border-[var(--kitup-color-muted)]/20 px-4 py-5 text-sm text-[var(--kitup-color-muted)]">
        <LegalLinks compact />
      </footer>
    </div>
  );
}
